import { useCallback, useEffect } from 'react'
import { isNative } from '@dhl/webview'

const createScriptElement = (id: string, nonce: string, attributes: Record<string, string> = {}) => {
  const scriptElement = document.createElement('script')
  scriptElement.id = id
  scriptElement.type = 'text/javascript'
  scriptElement.nonce = nonce

  return Object.keys(attributes).reduce((script, key) => {
    script.setAttribute(key, attributes[key])
    return script
  }, scriptElement)
}

let attempts = 0
const maxAttempts = 10

type Props = {
  id: string | undefined
  nonce: string
  shouldInitalize: boolean
  onLoad: () => void
}

export const useOneTrustCookieBanner = ({ id, nonce, shouldInitalize, onLoad }: Props): void => {
  const scriptUrl = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js'

  const waitForOneTrustObjectInWindow = useCallback(() => {
    if (window.OneTrust && window.OneTrust?.OnConsentChanged && window.OneTrust?.AllowAll) {
      onLoad()
      return
    }

    attempts += 1
    if (attempts < maxAttempts) {
      setTimeout(waitForOneTrustObjectInWindow, 100)
    }
  }, [onLoad])

  const conditions: ((s: HTMLScriptElement) => boolean)[] = [
    (s) => s.id === '1-trust',
    (s) => s.id === '1-trust-wrapper',
    (s) => s.src.includes('cdn.cookielaw.org'),
  ]

  let scriptAlreadyAdded = false

  for (let i = 0; i < document.scripts.length; i += 1) {
    const script = document.scripts[i]

    if (conditions.some((c) => c(script))) {
      scriptAlreadyAdded = true
    }
  }

  useEffect(() => {
    if (!id || !shouldInitalize || scriptAlreadyAdded) {
      return
    }

    const stubScript = createScriptElement('1-trust', nonce, {
      'data-document-language': 'true',
      charset: 'UTF-8',
      'data-domain-script': id,
      src: scriptUrl,
    })

    const shouldHideModal = isNative()

    const wrapperScript = createScriptElement('1-trust-wrapper', nonce)
    wrapperScript.innerText = `function OptanonWrapper() { 
      const otConsentSdk = document.getElementById("onetrust-consent-sdk");
      otConsentSdk.style.display = "${shouldHideModal ? 'none' : 'block'}";
      ${waitForOneTrustObjectInWindow()};
    }`

    document.head.appendChild(stubScript)
    document.head.appendChild(wrapperScript)
  }, [id, nonce, shouldInitalize, scriptAlreadyAdded, waitForOneTrustObjectInWindow])
}
